import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toggle, container, content, element, tabs, tab, search } from './styles.js';
import { getLocalFeatures, useFeatureContext } from 'contexts/FeatureContext.jsx';
import { isProduction } from 'constants/gcp.js';
import { DEV_SETTINGS, DEV_SETTINGS_LOCAL_STORAGE_KEY } from 'constants/devSettings.js';
import localPersistenceHelper from 'helpers/localPersistenceHelper.js';

const TABS = {
  ALL: 'all',
  RECENTS: 'recents',
  SETTINGS: 'settings',
};

const localStorageFeatures = getLocalFeatures();
const recentsFeatures = Object.keys(localStorageFeatures).map(key => key);
const localStorageSettings = localPersistenceHelper.getItem({
  key: DEV_SETTINGS_LOCAL_STORAGE_KEY,
}) || [];

const settings = Object.values(DEV_SETTINGS);

const FeaturesWindow = () => {
  const featureContext = useFeatureContext();
  const [features, setFeatures] = useState({});
  const [featuresList, setFeaturesList] = useState([]);
  const [isToggled, setIsToggled] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [selectedSettings, setSelectedSettings] = useState([...localStorageSettings]);
  const [recents, setRecents] = useState(recentsFeatures);
  const [selectedTab, setSelectedTab] = useState(TABS.ALL);
  const [searchInput, setSearchInput] = useState('');
  const authUser = useSelector((state) => state.sessionState.authUser);
  const isSuperadmin = authUser?.isSuperadmin;

  useEffect(() => {
    if (featureContext.loading) return;
    const selectedFeaturesSet = new Set(selectedFeatures);
    const features = Object.keys(featureContext.features).reduce((acc, featureFlag) => {
      const featureName = featureFlag.substring(0, featureFlag.lastIndexOf('Enabled'));
      acc[featureName] = featureContext.features[featureFlag];
      if (acc[featureName]) {
        selectedFeaturesSet.add(featureName);
      }
      return acc;
    }, {});
    setFeatures(features);
    setFeaturesList(Object.keys({ ...features }));
    setSelectedFeatures([...selectedFeaturesSet]);
  }, [featureContext.loading, featureContext.features]);

  const isDev = process.env.NODE_ENV === 'development';
  if (isProduction && !isDev && !isSuperadmin) return null;
  if (navigator.userAgent.includes(' BambuserLiveShopping/1.0')) return null;

  const handleToggleButtonClick = () => { setIsToggled(prev => !prev); };
  const handleTabChange = (tab) => { setSelectedTab(tab); };

  const handleSearchChange = ({ target }) => setSearchInput(target.value);

  const handleInputChange = ({ target }) => {
    const selected = selectedFeatures;
    const featureName = target.name;

    if (selectedTab === TABS.SETTINGS) {
      if (target.checked) setSelectedSettings([...selectedSettings, featureName]);
      else setSelectedSettings(selectedSettings.filter(feature => feature !== featureName));
      return;
    }

    if (selected.includes(featureName)){
      setSelectedFeatures(selected.filter(feature => feature !== featureName));
      setPendingUpdates({
        ...pendingUpdates,
        [`${featureName}`]: false,
      });
      return;
    }
    setSelectedFeatures([...selected, featureName]);
    setPendingUpdates({
      ...pendingUpdates,
      [`${featureName}`]: true,
    });
  };

  const handleSubmit = () => {
    localPersistenceHelper.setItem({
      key: DEV_SETTINGS_LOCAL_STORAGE_KEY,
      data: selectedSettings,
    });
    const updates = [];
    for (const [key, value] of Object.entries(pendingUpdates)) {
      updates.push(`${key}${value === false ? '=false' : ''}`);
    }
    let url = window.location.href;

    if (url.indexOf('?') > -1){
      url = url.substring(0, url.indexOf('?'));
    }
    window.location.href = `${url}?features=${updates.join(',')}`;
  };

  const handleClearOverrides = () => {
    if (!confirm('This will clear all your local overrides, are you sure?')) return;
    localPersistenceHelper.setItem({
      key: DEV_SETTINGS_LOCAL_STORAGE_KEY,
      data: [],
    });
    let url = window.location.href;
    if (url.indexOf('?') > -1){
      url = url.substring(0, url.indexOf('?'));
    }
    window.location.href = `${url}?features=clear`;
  };

  const getFeaturesList = () => {
    let list = [];
    if (selectedTab === TABS.ALL) list = featuresList;
    if (selectedTab === TABS.RECENTS) list = recents;
    if (selectedTab === TABS.SETTINGS) list = settings;

    return list.filter((feature) => {
      return feature.toLowerCase().includes(searchInput.toLowerCase());
    });
  };

  return (
    <>
      <button className={isToggled ? `${toggle} open` : toggle} onClick={handleToggleButtonClick}>Features &amp; Settings</button>
      <div className={isToggled ? `${container} open` : container}>
        <div className={tabs}>
          <button className={tab} onClick={() => { handleTabChange(TABS.ALL);}}>All</button>
          <button className={tab} onClick={() => { handleTabChange(TABS.RECENTS);}}>Recent</button>
          <button className={tab} onClick={() => { handleTabChange(TABS.SETTINGS);}}>Settings</button>
        </div>
        <input className={search} type="text" placeholder="Search..." onChange={handleSearchChange} />
        <div className={content}>
          {getFeaturesList().sort().map(feature => (
            <label className={element} key={feature}>
              <input
                name={feature}
                type="checkbox"
                checked={selectedFeatures.includes(feature) || selectedSettings.includes(feature)}
                onChange={handleInputChange}
              />
              {feature}
            </label>
          ),
          )}
        </div>
        <button type="submit" onClick={handleSubmit}>Apply</button>
        <button type="submit" onClick={handleClearOverrides}>Clear local overrides</button>
      </div>
    </>
  );
};

export default FeaturesWindow;
